/**
 * Created by yilmaz on 08.06.24.
 * Project: alea-carehome-web
 */

import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@chakra-ui/react';
import './selection.scss';
// import bodyData from './tooltips.json';
import Tooltip from './elements/Tooltip';
import Modal from './elements/Modal';
import { useTranslation } from 'react-i18next';

// type BodyParts = {
//   [key: string]: string;
// };

// type BodyTooltipValues = {
//   front: BodyParts;
//   back: BodyParts;
// };

type BedsoreSelectionProps = {
  gender: 'male' | 'female' | 'diverse';
  view: 'front' | 'back';
  tooltipPlacement?: 'right' | 'left';
  existingSelections?: BedSoreSelections;
  updateSelections?: (selections: BedSoreSelections) => void;
  onDelete?: (id: string, item: BedSoreSelectionItem) => void;
  onUpdate?: (id: string, item: BedSoreSelectionItem) => void;
  onCreate?: (id: string, item: BedSoreSelectionItem) => void;
  title: string;
  disableTooltips?: boolean;
};

const capilaizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
// Lazy load the SVG components
const FemaleBodyBack = React.lazy(() => import('./positions/FemaleBodyBack'));
const FemaleBodyFront = React.lazy(() => import('./positions/FemaleBodyFront'));
const MaleBodyBack = React.lazy(() => import('./positions/MaleBodyBack'));
const MaleBodyFront = React.lazy(() => import('./positions/MaleBodyFront'));
const components = {
  FemaleBodyBack,
  FemaleBodyFront,
  MaleBodyBack,
  MaleBodyFront,
};

// const typedBodyData = bodyData as BodyTooltipValues;

export type BedSoreSelectionItem = {
  risk: string;
  note: string;
  _new?: boolean;
};

export type BedSoreSelections = {
  [key: string]: BedSoreSelectionItem;
};

type Action = 'add' | 'remove';

/**
 * A small utility function to add or remove class names from an SVG element.
 * @param element
 * @param action
 * @param classNames
 */
function modifySvgClassNames(
  element: SVGElement,
  action: Action,
  classNames: string[]
): void {
  const existingClasses = element.getAttribute('class') || '';
  let classArray = existingClasses.split(' ');

  classNames.forEach((className) => {
    if (action === 'add' && !classArray.includes(className)) {
      classArray.push(className);
    } else if (action === 'remove') {
      classArray = classArray.filter((classItem) => classItem !== className);
    }
  });

  element.setAttribute('class', classArray.join(' '));
}

// The state of the tooltip, it's not quite optimized.
// we get the content of the tooltip from the JSON file based on the ID of
// the circle element.
type ToolTipState = {
  visible: boolean;
  content: string;
  id: string;
  position: {
    direction: string;
    verticalDistance: number;
    boundaryDistance: number;
    placementDistance: number;
  };
};

type ModalState = {
  visible: boolean;
  id: string;
};

const BedsoreSelection: React.FC<BedsoreSelectionProps> = ({
  gender,
  view,
  tooltipPlacement = 'right',
  existingSelections,
  title,
  updateSelections,
  disableTooltips = false,
  onUpdate,
  onDelete,
  onCreate,
}) => {
  const { t } = useTranslation();

  // This for the keep track of the selections, a good way might be, leave this
  // to the parent component and pass the selections as props and trust that, but
  // at the moment we are keeping it here.
  const [selections, setSelection] = useState<BedSoreSelections>(
    existingSelections || {}
  );

  // if existingSelections get updated from the parent, we update the state
  useEffect(() => {
    if (existingSelections) {
      setSelection(existingSelections);
    }
  }, [existingSelections]);

  // The state of the tooltip
  const [tooltip, setTooltip] = useState<ToolTipState>({
    visible: false,
    content: '',
    id: '',
    position: {
      direction: 'left',
      verticalDistance: 0,
      boundaryDistance: 0,
      placementDistance: 200,
    },
  });
  // modal uses the same position as the tooltip, so we don't put here again to inherit from the tooltip
  // we either way have to display the tooltip while displaying the modal.
  const [modal, setModal] = useState<ModalState>({
    visible: false,
    id: '',
  });
  // This is an object to cache event handler. When we want to remove event listeners
  // we have to remove the exact copy of the event listener that we added.
  // so we keep track of all the event listeners in this object.
  const eventHandlers = useRef(
    new Map<
      string,
      {
        mouseover: (e: Event) => void;
        mouseout: (e: Event) => void;
        click: (e: Event) => void;
      }
    >()
  );
  // this is main SVG element
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);

  // instead of useRef we use useCallback to keep the reference of the SVG element
  const svgRef = useCallback(
    (svgElement: SVGSVGElement) => {
      if (svgElement !== null) {
        if (!disableTooltips) {
          createClickCircle(svgElement);
          addEventListeners(svgElement);
        }
        setSvgElement(svgElement);
      }

      return svgElement;
    },
    [disableTooltips]
  );

  // we keep track of selection changes and update the circle elements if they're selected
  // this will be triggerd in the first load, due the svgElement is not ready yet and once it's ready
  // after thant, only when the selections change.
  useEffect(() => {
    if (svgElement) {
      const circles = svgElement.querySelectorAll('circle');
      circles.forEach((circle) => {
        const elementID = circle.getAttribute('id');
        if (!elementID || elementID === 'click_container') return;

        // first clear all the classes
        modifySvgClassNames(circle, 'remove', ['has-Threat', 'has-Wound']);

        const selection = selections[elementID];
        // if selection exists, add the class to the circle
        if (selection) {
          modifySvgClassNames(circle, 'add', [`has-${selection.risk}`]);
        }
      });
    }
  }, [selections, svgElement]);

  /**
   * Create an outer circle for the click event and attaches it to the main
   * SVG element to be used later.
   * @param svgElement
   */
  const createClickCircle = (svgElement: SVGSVGElement) => {
    const outerCircleElement = svgElement.querySelector(
      '#click_container'
    ) as SVGCircleElement;

    if (!outerCircleElement) {
      const outerCircleElement = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'circle'
      );
      outerCircleElement.setAttribute('id', 'click_container');

      outerCircleElement.setAttribute('cx', '0');
      outerCircleElement.setAttribute('cy', '0');
      outerCircleElement.setAttribute('r', '0');
      outerCircleElement.setAttribute('fill', '#fff');
      outerCircleElement.setAttribute('stroke', 'none');
      outerCircleElement.setAttribute('stroke-width', '0');

      // we add this to the main SVG element, but later we will move it to the parent of the circle
      svgElement.appendChild(outerCircleElement);
    }

    return outerCircleElement;
  };

  /**
   * All at once we register our event listeners to the circles in the SVG element.
   * @param svgElement
   */
  const addEventListeners = (svgElement: SVGSVGElement) => {
    const circles = svgElement.querySelectorAll('circle');

    circles.forEach((circle) => {
      const elementID = circle.getAttribute('id');
      if (!elementID || elementID === 'click_container') return;
      // const content = typedBodyData[view][elementID as string];
      const content = elementID;

      // mark the circle enabled. Otherwise, it won't get the stroke.
      modifySvgClassNames(circle, 'add', ['enabled']);

      const handleCircleMouseOver = (e: Event) =>
        handleMouseOver(svgElement, e as MouseEvent, content, elementID);
      const handleCircleMouseOut = () => handleMouseOut();
      const handleCircleClick = (e: Event) =>
        handleClick(e as MouseEvent, svgElement, circle);

      eventHandlers.current.set(elementID, {
        mouseover: handleCircleMouseOver,
        mouseout: handleCircleMouseOut,
        click: handleCircleClick,
      });

      circle.addEventListener('mouseover', handleCircleMouseOver);
      circle.addEventListener('mouseout', handleCircleMouseOut);
      circle.addEventListener('click', handleCircleClick);
    });
  };

  /**
   * Clear all event listeners from the circles in the SVG element.
   * @param svgElement
   */
  const clearEventListeners = (svgElement: SVGSVGElement) => {
    const circles = svgElement.querySelectorAll('circle');

    circles.forEach((circle) => {
      const elementID = circle.getAttribute('id');
      if (!elementID || elementID === 'click_container') return;
      const handlers = eventHandlers.current.get(elementID);
      if (handlers) {
        circle.removeEventListener('mouseover', handlers.mouseover);
        circle.removeEventListener('mouseout', handlers.mouseout);
        circle.removeEventListener('click', handlers.click);
        eventHandlers.current.delete(elementID);
      }
    });
  };

  /**
   * Calculate the position of the tooltip based on the mouse event and the SVG element.
   * We calculate the position based on the circle we hover on or click.
   * We calculate to the left or right of the circle based on the tooltipPlacement prop and
   * the distance from the top the position the tooltip line correctly.
   * @param svgElement
   * @param e
   */
  const calculatePosition = (svgElement: SVGSVGElement, e: MouseEvent) => {
    const svgRect = svgElement.getBoundingClientRect();
    const circleRect = (e.target as HTMLElement).getBoundingClientRect();

    const distanceFromLeft = circleRect.left - svgRect.left;
    const distanceFromRight = svgRect.right - circleRect.right;

    // const circleX = circleRect.left + circleRect.width / 2 - svgRect.left;
    const circleY = circleRect.top + circleRect.height / 2 - svgRect.top;

    return {
      direction: tooltipPlacement,
      verticalDistance: circleY,
      boundaryDistance:
        tooltipPlacement === 'left' ? distanceFromLeft : distanceFromRight,
      placementDistance: 200,
    };
  };

  const handleMouseOver = (
    svgElement: SVGSVGElement,
    e: MouseEvent,
    content: string,
    id: string
  ) => {
    const tooltipPosition = calculatePosition(svgElement, e);
    setTooltip({
      visible: true,
      content,
      id,
      position: tooltipPosition,
    });
  };

  const handleMouseOut = () => {
    setTooltip({
      visible: false,
      content: '',
      id: '',
      position: {
        direction: '',
        boundaryDistance: 0,
        verticalDistance: 0,
        placementDistance: 0,
      },
    });
  };

  /**
   * When we click, we remove the event listeners from the circles and show the modal.
   * @param e
   * @param svgElement
   * @param circle
   */
  const handleClick = (
    e: MouseEvent,
    svgElement: SVGSVGElement,
    circle: SVGCircleElement
  ) => {
    const clickContainerCircle = svgElement.querySelector('#click_container');
    if (clickContainerCircle) {
      const cx = parseFloat(circle.getAttribute('cx')!);
      const cy = parseFloat(circle.getAttribute('cy')!);
      const r = parseFloat(circle.getAttribute('r')!);

      const outerCircleRadius = r + 6; // Adjust the fixed radius for the outer circle
      clickContainerCircle.setAttribute('cx', cx.toString());
      clickContainerCircle.setAttribute('cy', cy.toString());
      clickContainerCircle.setAttribute('r', outerCircleRadius.toString());

      // We added and empty circle to indicate the clicked circle. It initially lies on top of the clicked circle.
      // What we need to do is to move it to a position that comes before the first circle in the parent.
      // This is due to layers and orders of an SVG element. First one stays at the bottom.
      // Find the parent of the clicked circle and move the outer circle to the first position.
      const container = circle.parentNode;
      // Check if the click container is already in the parent
      const clickContainerInParent = container?.contains(clickContainerCircle);
      if (!clickContainerInParent) {
        // Find the first circle in the parent and insert the click container before it.
        const firstCircle = container?.querySelector('circle');
        // Remove the click container from the parent and insert it before the first circle.
        svgElement.removeChild(clickContainerCircle);
        container?.insertBefore(clickContainerCircle, firstCircle!);
      }
    }

    const elementID = circle.getAttribute('id');
    // const content = typedBodyData[view][elementID as string];
    const content = elementID as string;

    // first clear the event listeners
    clearEventListeners(svgElement);

    setModal({
      visible: true,
      id: elementID as string,
    });

    const tooltipPosition = calculatePosition(svgElement, e);
    setTooltip({
      visible: true,
      content,
      id: elementID as string,
      position: tooltipPosition,
    });
  };

  /**
   * Clean up the modal and the tooltip and add the event listeners back to the circles.
   * @param id
   * @param note
   * @param risk
   * @param action
   */
  const cleanUpModal = (
    id: string,
    note: string,
    risk: string,
    action: 'add' | 'remove' | 'update'
  ) => {
    const current = selections[id];
    const wasNew = current?._new == true;

    const currentSelections = {
      ...selections,
    };
    // update the list with new addition
    if (action === 'add') {
      currentSelections[id] = {
        risk,
        note,
        _new: false,
      };
    }

    const selectedItem = currentSelections[id];

    if (action === 'remove') {
      delete currentSelections[id];
    }

    setSelection(currentSelections);
    setModal({ ...modal, visible: false });
    setTooltip({ ...tooltip, visible: false });
    addEventListeners(svgElement!);
    // clear up #click_container
    const clickContainerCircle = svgElement!.querySelector('#click_container');
    if (clickContainerCircle) {
      clickContainerCircle.setAttribute('r', '0');
    }

    // if provided call the updateSelections from the props
    if (updateSelections) {
      updateSelections(currentSelections);
    }

    if (action == 'add') {
      if (wasNew && onCreate) {
        onCreate(id, selectedItem);
      } else if (!wasNew && onUpdate) {
        onUpdate(id, selectedItem);
      }
    }

    if (action == 'remove') {
      if (!wasNew && onDelete && selectedItem) {
        onDelete(id, selectedItem);
      }
    }
  };

  const handleSave = (id: string, note: string, risk: string) => {
    cleanUpModal(id, note, risk, 'add');
  };

  const handleModalClose = (id: string) => {
    cleanUpModal(id, '', '', 'remove');
  };

  const handleTabSwitch = (id: string, risk: string) => {
    const current = selections[id];
    // we try to determine if this is a new item or not
    // if this is not in the list, then it's new, or take the value from
    // the current selection
    const _new = current ? current._new : true;
    setSelection({
      ...selections,
      [id]: { risk, note: current?.note || '', _new },
    });
  };

  const renderComponent = () => {
    const key =
      `${capilaizeFirstLetter(gender)}Body${capilaizeFirstLetter(view)}` as keyof typeof components;
    const Component = components[key];
    return <Component className="svg-body" ref={svgRef} />;
  };

  return (
    <Box px="15px" py="10px" bg="white">
      {title && (
        <h4
          style={{
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          {title}
        </h4>
      )}
      <div className="interactive-body">
        <Suspense fallback={<div>{t('general.loading')}</div>}>
          {renderComponent()}
        </Suspense>
        {tooltip.visible && (
          <Tooltip
            id={tooltip.id}
            content={tooltip.content}
            direction={tooltipPlacement}
            verticalDistance={tooltip.position.verticalDistance}
            boundaryDistance={tooltip.position.boundaryDistance}
            placementDistance={tooltip.position.placementDistance}
            selection={selections[tooltip.id]}
            modalVisible={modal.visible}
          />
        )}

        {modal.visible && (
          <Modal
            id={tooltip.id}
            selection={selections[tooltip.id]}
            content={tooltip.content}
            direction={tooltipPlacement}
            verticalDistance={tooltip.position.verticalDistance}
            boundaryDistance={tooltip.position.boundaryDistance}
            placementDistance={tooltip.position.placementDistance}
            handleSave={handleSave}
            handleModalClose={handleModalClose}
            handleTabSwitch={handleTabSwitch}
          />
        )}
      </div>
    </Box>
  );
};

export default BedsoreSelection;
